import React from "react";
import { RefreshCw, Database, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import {
  useGetCourtScraperStatsQuery,
  useGetCourtFilterOptionsQuery,
} from "@/redux/api/courtScraper.rtkq";
import { KpiCards } from "@/components/court-scraper/KpiCards";
import { DistributionCharts } from "@/components/court-scraper/DistributionCharts";
import { DocumentsTable } from "@/components/court-scraper/DocumentsTable";

export const CourtScraperPage: React.FC = () => {
  const { data: statsData, isLoading: isStatsLoading, isFetching: isStatsFetching, refetch } =
    useGetCourtScraperStatsQuery(undefined, {
      pollingInterval: 60000, // Auto-refresh every 60s
    });

  const { refetch: refetchFilters } = useGetCourtFilterOptionsQuery();

  const handleManualRefresh = () => {
    refetch();
    refetchFilters();
  };

  const formattedTime = statsData?.refreshedAt
    ? new Date(statsData.refreshedAt).toLocaleTimeString("he-IL", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : null;

  return (
    <div className="min-h-screen bg-slate-50/70" dir="rtl">
      {/* Top Page Header */}
      <div className="sticky top-12 z-10 border-b border-slate-200 bg-white shadow-xs max-md:top-16 md:top-0">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                  <Database className="w-5 h-5" />
                </div>
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                  דשבורד נט המשפט — סריקה ומסד מסמכים
                </h1>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <ShieldCheck className="w-3 h-3" />
                  Super Admin
                </span>
              </div>
              <p className="text-xs text-slate-500">
                מעקב בזמן אמת אחר קצב סריקת פסקי הדין, פיצול עמודים, חילוץ Gemini ומשפך ה-NLP.
              </p>
            </div>

            <div className="flex items-center gap-2.5 w-full sm:w-auto justify-between sm:justify-end">
              {formattedTime && (
                <span className="text-xs text-slate-400 font-mono hidden md:inline">
                  עודכן לאחרונה: {formattedTime}
                </span>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={handleManualRefresh}
                disabled={isStatsFetching}
                className="text-xs flex items-center gap-1.5 h-8 bg-white border-slate-200 hover:bg-slate-50"
              >
                <RefreshCw
                  className={`w-3.5 h-3.5 text-slate-600 ${isStatsFetching ? "animate-spin" : ""}`}
                />
                <span>רענן נתונים</span>
              </Button>

              <Button
                variant="ghost"
                size="sm"
                asChild
                className="text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 h-8 flex items-center gap-1"
              >
                <Link to="/super-admin">
                  <span>ניהול מערכת</span>
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Dashboard */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Section 1: KPI Summary & Progress Funnel */}
        <KpiCards kpis={statsData?.kpis} isLoading={isStatsLoading} />

        {/* Section 2: Distribution Charts */}
        <DistributionCharts
          courts={statsData?.courts}
          decisionTypes={statsData?.decisionTypes}
          isLoading={isStatsLoading}
        />

        {/* Section 3: Document Explorer & Paginated Database */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <span>מאגר מסמכים ופסקי דין</span>
              <span className="text-xs font-normal text-slate-400">
                (חיפוש, סינון, צפייה ב-PDF וחילוצי Gemini)
              </span>
            </h2>
          </div>
          <DocumentsTable />
        </div>
      </div>
    </div>
  );
};

export default CourtScraperPage;
